const PACKET = 48064731250873;
const MAX_RESPONSE_BYTES = 256 * 1024;

function checkedCart(cart) {
  if (!cart || cart.currency !== 'AUD' || typeof cart.token !== 'string' || !cart.token || cart.token.length > 256
    || !Array.isArray(cart.items) || cart.items.some(item => !Number.isSafeInteger(item.variant_id)
      || !Number.isSafeInteger(item.quantity) || item.quantity < 1)) throw new Error('CART_FACTS_UNAVAILABLE');
  return cart;
}

export async function ensureSardinesPacket(request, pending) {
  const before = checkedCart(await request('cart.js'));
  if (before.items.some(item => item.variant_id === PACKET)) {
    pending.removeItem(before.token);
    return before;
  }
  // A lost mutation response can still mean the packet was added. Never repeat that write.
  if (pending.getItem(before.token) !== null) throw new Error('CART_ADD_OUTCOME_UNKNOWN');
  pending.setItem(before.token, 'pending');
  await request('cart/add.js', { items: [{ id: PACKET, quantity: 1 }] });
  const after = checkedCart(await request('cart.js'));
  if (!after.items.some(item => item.variant_id === PACKET)
    || before.items.some(item => after.items.filter(line => line.variant_id === item.variant_id)
      .reduce((sum, line) => sum + line.quantity, 0) < before.items.filter(line => line.variant_id === item.variant_id)
        .reduce((sum, line) => sum + line.quantity, 0))) throw new Error('CART_READBACK_MISMATCH');
  pending.removeItem(before.token);
  return after;
}

async function cartRequest(root, path, body) {
  const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 10000);
  try {
    const response = await fetch(root + path, { method: body ? 'POST' : 'GET', credentials: 'same-origin',
      redirect: 'error', signal: controller.signal, headers: { Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}) });
    if (!response.ok || !response.headers.get('content-type')?.includes('application/json') || !response.body) throw new Error('CART_REQUEST_REFUSED');
    const reader = response.body.getReader(), chunks = []; let size = 0;
    for (;;) {
      const part = await reader.read(); if (part.done) break;
      size += part.value.byteLength;
      if (size > MAX_RESPONSE_BYTES) { await reader.cancel(); throw new Error('CART_RESPONSE_TOO_LARGE'); }
      chunks.push(part.value);
    }
    const bytes = new Uint8Array(size); let offset = 0;
    for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.byteLength; }
    return JSON.parse(new TextDecoder().decode(bytes));
  } finally { clearTimeout(timer); }
}

export async function startSardinesCart(element, browser) {
  const query = new URLSearchParams(browser.location.search);
  if (query.get('hsc_sardines_claim') !== '1' || query.get('variant') !== String(PACKET)) return;
  element.hidden = false;
  const root = element.dataset.cartRoot;
  if (!/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?$/.test(root) || !browser.navigator.locks) {
    element.textContent = element.dataset.failed; return;
  }
  element.textContent = element.dataset.working;
  try {
    // Same-origin Web Locks coordinate this component across tabs. No cart replacement/update.
    await browser.navigator.locks.request('hsc-sardines-cart', async () => {
      const storage = browser.localStorage, prefix = 'hsc-sardines-pending:';
      const pending = { getItem: token => storage.getItem(prefix + token),
        setItem: (token, state) => storage.setItem(prefix + token, state), removeItem: token => storage.removeItem(prefix + token) };
      await ensureSardinesPacket((path, body) => cartRequest(root, path, body), pending);
    });
    browser.location.assign(root + 'cart');
  } catch (error) {
    element.textContent = element.dataset.failed;
    element.dataset.cartOutcome = 'held';
  }
}

if (typeof document !== 'undefined') {
  const element = document.querySelector('[data-hsc-sardines-claim]');
  if (element) startSardinesCart(element, window);
}
